/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  ExecutionProfileOption,
  ExecutionProfileOption$inboundSchema,
} from "./executionprofileoption.js";

/**
 * Why a server without a choice gets none on its own.
 */
export const GapReason = {
  SelectionRequired: "selection_required",
  PersonalRequiresUser: "personal_requires_user",
  NoClient: "no_client",
  SetupIncomplete: "setup_incomplete",
} as const;
/**
 * Why a server without a choice gets none on its own.
 */
export type GapReason = OpenEnum<typeof GapReason>;

export const ExecutionProfileChoiceSource = {
  Admin: "admin",
  Issuance: "issuance",
  Backfill: "backfill",
  Automatic: "automatic",
} as const;
export type ExecutionProfileChoiceSource = OpenEnum<
  typeof ExecutionProfileChoiceSource
>;

/**
 * One MCP server an assistant's turns reach through Speakeasy.
 */
export type ExecutionProfileServer = {
  /**
   * The chosen auth client. Absent with a choice means no upstream authentication.
   */
  authClientId?: string | undefined;
  /**
   * Whether the server has a choice.
   */
  chosen: boolean;
  /**
   * Why a server without a choice gets none on its own.
   */
  gapReason?: GapReason | undefined;
  /**
   * The MCP server.
   */
  mcpServerId: string;
  /**
   * Whether the server offers no enabled auth client and has no upstream credentials left to record as one, so no-auth can be chosen.
   */
  noAuthAllowed: boolean;
  /**
   * The credentials that can be chosen.
   */
  options: Array<ExecutionProfileOption>;
  executionProfileChoiceSource?: ExecutionProfileChoiceSource | undefined;
};

/** @internal */
export const GapReason$inboundSchema: z.ZodMiniType<GapReason, unknown> =
  openEnums.inboundSchema(GapReason);

/** @internal */
export const ExecutionProfileChoiceSource$inboundSchema: z.ZodMiniType<
  ExecutionProfileChoiceSource,
  unknown
> = openEnums.inboundSchema(ExecutionProfileChoiceSource);

/** @internal */
export const ExecutionProfileServer$inboundSchema: z.ZodMiniType<
  ExecutionProfileServer,
  unknown
> = z.pipe(
  z.object({
    auth_client_id: z.optional(z.string()),
    chosen: z.boolean(),
    gap_reason: z.optional(GapReason$inboundSchema),
    mcp_server_id: z.string(),
    no_auth_allowed: z.boolean(),
    options: z.array(ExecutionProfileOption$inboundSchema),
    source: z.optional(ExecutionProfileChoiceSource$inboundSchema),
  }),
  z.transform((v) => {
    return remap$(v, {
      "auth_client_id": "authClientId",
      "gap_reason": "gapReason",
      "mcp_server_id": "mcpServerId",
      "no_auth_allowed": "noAuthAllowed",
      "source": "executionProfileChoiceSource",
    });
  }),
);

export function executionProfileServerFromJSON(
  jsonString: string,
): SafeParseResult<ExecutionProfileServer, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ExecutionProfileServer$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ExecutionProfileServer' from JSON`,
  );
}
